import { Platform, View } from "@-/com.npmjs.react-native";
import Animated, {
  type SharedValue,
  useAnimatedRef,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { ScrollEdgeTopInsetContext } from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import {
  type ChinaMapRegionId,
  isChinaMapTraditionalLabelRegion,
} from "@-/dev.improve.haniverse.data.china-map";
import type { MapDownload } from "@-/dev.improve.haniverse.data.map-downloads";
import {
  createMapDownloadService,
  mapDownloadQueryKey,
  mapDownloadStore,
} from "@-/dev.improve.haniverse.lib.map-downloads";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { type ChinaTransport, greatCircleKm } from "@-/dev.improve.haniverse.data.china-transport";
import {
  type HanWallet,
  useHanWalletQueryOptions,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  type PronouncedGraphemes,
  usePronouncedGraphemesQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import { buildUnitSequence } from "@-/dev.improve.haniverse.lib.lesson.get";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import {
  resolveTravelOptions,
  SUGGESTED_START_ADCODE,
  type TravelOption,
  useTravelActions,
  useTravelState,
} from "@-/dev.improve.haniverse.lib.travel";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { PrimaryControlsStripToolbar } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { useBrowseLevelsStudyGraphemesQuery } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import ensureError from "@-/com.npmjs.ensure-error";
import { useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ChinaMapCanvas,
  type ChinaMapCameraControls,
  type ChinaMapFocusRequest,
  ChinaMapLoadingCanvas,
} from "#china-map-canvas";
import { MapWorkspace } from "#map-workspace";
import { ChinaMapBottomSheet } from "#china-map-bottom-sheet";
import { ChinaMapSheetButtons } from "#china-map-sheet-buttons";
import { ChinaMapSheetSlide } from "#china-map-sheet-slide";
import { ChinaMapSheetWallet } from "#china-map-sheet-wallet";
import { isChinaMapLocationMarkerHit } from "#china-map-location-marker";
import {
  ChinaMapChooseStartSheetContent,
  ChinaMapOverviewSheetContent,
} from "#china-map-overview-sheet-content";
import { ChinaMapPlaceCard } from "#china-map-place-card";
import {
  type ChinaMapPlaceView,
  createChinaMapPlaceViews,
  findChinaMapPlaceAt,
  findChinaMapPlaceView,
  getChinaMapUpcomingPlaces,
  getChinaMapVisiblePlaces,
} from "#china-map-places";
import { CHINA_MAP_REGION_COPY } from "#china-map-region-copy";
import type { ChinaMapTravelOverlay } from "#china-map-renderer-props";
import {
  CHINA_MAP_SHEET_BUTTONS_GAP,
  CHINA_MAP_SHEET_HEIGHTS,
  type ChinaMapSheetDetent,
} from "#china-map-sheet-heights";
import { useChinaMapOpeningSheetHeight } from "#use-china-map-opening-sheet-height";
import {
  ChinaMapInTransitCard,
  ChinaMapPlaceTravelActions,
  ChinaMapTravelOptionsCard,
} from "#china-map-travel-cards";
import { createLessonsUntilByCharacter, createLevelIndexByCharacter } from "#china-place-unlock";
import {
  HAS_MAP_REGION_CHOICE,
  resolveAvailableMapRegionId,
} from "#map-available-regions";
import { MapRegionTabs } from "#map-region-tabs";
import { useMapOfflineDownloads } from "#use-map-offline-downloads";
import { useIsOnboardingTour } from "@-/dev.improve.haniverse.lib.onboarding";
import { MapMoreActions } from "#map-more-actions";
import { MapQuestsSurface } from "#map-quests-surface";
import { useChinaMapColors } from "#use-china-map-colors";
import { usePublishLearnMapSheetTop } from "#use-publish-learn-map-sheet-top";

/** Nothing counts as studied until the study-data query settles, so the map renders immediately. */
const NO_STUDY_DATA_YET: ReadonlySet<string> = new Set();
const NOTHING_PRONOUNCED_YET: PronouncedGraphemes = { graphemes: new Set(), readings: new Set() };
const EMPTY_WALLET: HanWallet = { dragons: 0, han: 0, hanMiles: 0 };
/** The transport network until the map's download has brought it. */
const NO_TRANSPORT_YET: ChinaTransport = {
  nodes: [],
  places: [],
  routes: [],
  sources: { flightFareDate: null, flightsRetrievedAt: null, railRetrievedAt: null },
};
/** Quick picks offered in the sheet. */
const MAX_PLACE_CHIPS = 12;
const TRAILING_SLASHES_RE = /\/+$/;

/** A fresh sheet on each opening, so a previously collapsed sheet cannot be the next first frame. */
function useMapOpeningVisit(isCurrent: boolean): number {
  const [opening, setOpening] = useState({ isCurrent, visit: 0 });
  if (opening.isCurrent !== isCurrent) {
    setOpening({ isCurrent, visit: opening.visit + (isCurrent ? 1 : 0) });
  }
  return opening.visit;
}

interface ChinaMapSelection {
  /** The selected region: the province itself, or one of its cities. */
  placeAdcode: number;
  /** The province the map is zoomed into. */
  provinceAdcode: number;
}

/** How the map sits in Learn: whether it is the screen, and how far it has come in from the cards. */
export interface MapPresence {
  /** The map is Learn's home (where the app opens): its greeting heads the sheet. */
  isHome: boolean;
  /** The map has been opened, so it may download; until then it waits, ready, beneath the cards. */
  isLoadRequested: boolean;
  /** The map is Learn's screen: not closing, and no page is open over it. */
  isShown: boolean;
  /** The map's toolbar is the one on screen (from the press that opens the map). */
  isToolbarCurrent: boolean;
  /** 0 with the cards on screen, 1 with the map (`LearnMapMode.progress`). */
  progress: SharedValue<number>;
}

/**
 * The map, loading or loaded. It belongs to Learn (`MapLayer`), whose cards slide away over it; the
 * sheet and the buttons riding it slide in from the bottom on the same clock. It is mounted ahead
 * of time, beneath the cards, and downloads its map only once opened.
 */
export function MapContent({
  presence,
  welcome = null,
}: {
  presence: MapPresence;
  /** The greeting at the top of the overview, including while map/study data is unavailable. */
  welcome?: ReactNode;
}) {
  const [storedRegionId, setRegionId] = useDeviceSetting("mapRegion");
  const regionId = resolveAvailableMapRegionId(storedRegionId);
  const workerUrl = resolveHanWorkerUrl().replace(TRAILING_SLASHES_RE, "");
  const service = useMemo(
    () => createMapDownloadService({ store: mapDownloadStore, workerUrl }),
    [workerUrl],
  );
  const [initialFocus, setInitialFocus] = useState<ChinaMapFocusRequest["bbox"] | null>(null);
  const selectRegion = useCallback(
    (id: ChinaMapRegionId, bbox: ChinaMapFocusRequest["bbox"] | null = null) => {
      setInitialFocus(bbox);
      setRegionId(id);
    },
    [setRegionId],
  );
  const query = useQuery({
    queryKey: mapDownloadQueryKey(workerUrl, regionId),
    queryFn: ({ signal }) => service.load(regionId, signal),
    enabled: presence.isLoadRequested,
    networkMode: "always",
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const offlineMapsAction = useMapOfflineDownloads({
    enabled: presence.isLoadRequested,
    service,
    workerUrl,
  });
  // One page from the download's start to the loaded map, so that its sheet stays mounted when the
  // map arrives: a new native sheet takes a couple of frames to appear, and on a launch into the
  // home the greeting and the way into the cards blinked out in between.
  return (
    <MapPage
      initialFocus={initialFocus}
      isError={query.isError}
      key={regionId}
      mapDownload={query.data ?? null}
      offlineMapsAction={offlineMapsAction}
      onRetry={() => void query.refetch()}
      presence={presence}
      regionId={regionId}
      setRegionId={selectRegion}
      welcome={welcome}
    />
  );
}

/**
 * The map's top bar: its tabs, floating over the map. It takes over from Learn's toolbar only while
 * the map is the screen, so the shell's status band and fade follow whichever bar is showing.
 */
function MapToolbar({
  isCurrent,
  onSelectRegion,
  regionId,
}: {
  isCurrent: boolean;
  onSelectRegion: (regionId: ChinaMapRegionId) => void;
  regionId: ChinaMapRegionId;
}) {
  return (
    <>
      {isCurrent ? <PrimaryControlsStripTitle>Map</PrimaryControlsStripTitle> : null}
      <PrimaryControlsStripToolbar
        className="absolute inset-x-0 top-0"
        floating
        hideScrollEdgeEffect
        isCurrent={isCurrent}
        pointerEvents="box-none"
      >
        {HAS_MAP_REGION_CHOICE ? (
          <MapRegionTabs onSelect={onSelectRegion} regionId={regionId} />
        ) : null}
      </PrimaryControlsStripToolbar>
    </>
  );
}

function MapComingSoonSheetContent() {
  return (
    <View className="items-center justify-center p-6" testID="map-coming-soon">
      <Text className="text-center text-lg font-semibold text-foreground">Coming Soon</Text>
    </View>
  );
}

/** The download's status, in the sheet the map opens with, under the map home's greeting. */
function MapLoadingStatus({
  isError,
  onRetry,
  regionId,
}: {
  isError: boolean;
  onRetry?: () => void;
  regionId: ChinaMapRegionId;
}) {
  return (
    <View className="items-center justify-center gap-3 px-6 py-3" testID="map-online-status">
      <Text className="text-center text-lg font-semibold text-foreground">
        {isError ? "Map needs an internet connection" : "Loading map…"}
      </Text>
      <Text className="text-center text-sm text-muted-foreground">
        {isError
          ? "Connect to the internet and retry, or choose a downloaded map. Offline maps can be managed in the More menu."
          : `Opening ${CHINA_MAP_REGION_COPY[regionId].label}. Download it from Offline maps in the More menu to use it without a connection.`}
      </Text>
      {isError && onRetry !== undefined ? (
        <Button onPress={onRetry} testID="map-retry-button">
          <Text>Retry</Text>
        </Button>
      ) : null}
    </View>
  );
}

/**
 * The page before the map's content has mounted (`MapLoadingShell`: the first frame and the
 * router fallback), which starts and awaits no query: the loaded page's floating toolbar and canvas
 * surface, with the map's silhouette framed exactly where the map opens, above the sheet it opens
 * with. The status sits where that sheet will, under the map home's greeting, so the way into the
 * cards never waits for the map. `MapPage` draws the same while its own map downloads.
 */
export function LoadingMap({
  presence,
  regionId,
  setRegionId,
  welcome,
}: {
  presence: MapPresence;
  regionId: ChinaMapRegionId;
  setRegionId: (regionId: ChinaMapRegionId) => void;
  welcome: ReactNode;
}) {
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const openingSheetHeight = useChinaMapOpeningSheetHeight();
  const sheetTop = useSharedValue(0);
  const pageRef = useAnimatedRef<View>();
  const isOnboardingTour = useIsOnboardingTour();
  const openingVisit = useMapOpeningVisit(presence.isToolbarCurrent);
  const isComingSoon = regionId !== "mainland";
  const sheetWelcome = isComingSoon ? null : welcome;
  const sheetDetent: ChinaMapSheetDetent = sheetWelcome === null ? "peek" : "home";
  const sheetBottomInset =
    sheetDetent === "home" ? openingSheetHeight : CHINA_MAP_SHEET_HEIGHTS.peek;
  const controlsBottomInset =
    Platform.OS === "web" ? 16 : sheetBottomInset + CHINA_MAP_SHEET_BUTTONS_GAP;
  usePublishLearnMapSheetTop({
    isSheetShown: !isOnboardingTour,
    pageRef,
    progress: presence.progress,
    sheetTop,
  });
  return (
    <Animated.View className="flex-1 bg-background" ref={pageRef} testID="map-page">
      <MapToolbar
        isCurrent={presence.isToolbarCurrent}
        onSelectRegion={setRegionId}
        regionId={regionId}
      />
      <MapWorkspace
        controls={
          <ChinaMapSheetSlide progress={presence.progress} testID="map-sheet-buttons-slide">
            {/* Where the loaded map's sheet buttons put it: over the sheet's left edge. */}
            {isOnboardingTour ? null : (
              <View
                className="absolute left-4"
                pointerEvents="box-none"
                style={{ bottom: controlsBottomInset }}
                testID="map-more-actions"
              >
                <MapMoreActions camera={null} offlineMaps={null} />
              </View>
            )}
          </ChinaMapSheetSlide>
        }
        details={
          <ChinaMapSheetSlide
            className="z-20"
            progress={presence.progress}
            testID="map-sheet-slide"
          >
            {isOnboardingTour ? null : (
              <ChinaMapBottomSheet
                animatedPosition={sheetTop}
                corner={
                  <ChinaMapSheetWallet isDepositTarget={presence.isToolbarCurrent} wallet={null} />
                }
                detent={sheetDetent}
                key={openingVisit}
                restingKey={`${regionId}:loading`}
                topInset={toolbarHeight}
                welcome={sheetWelcome}
              >
                {isComingSoon ? (
                  <MapComingSoonSheetContent />
                ) : (
                  <MapLoadingStatus isError={false} regionId={regionId} />
                )}
              </ChinaMapBottomSheet>
            )}
          </ChinaMapSheetSlide>
        }
        map={
          <FrostedBackdropTarget active={presence.isShown} className="flex-1">
            <ChinaMapLoadingCanvas
              bottomInset={Platform.OS === "web" ? 0 : sheetBottomInset}
              regionId={regionId}
              topInset={Platform.OS === "web" ? 0 : toolbarHeight}
            />
          </FrostedBackdropTarget>
        }
        showDetails={!isOnboardingTour}
        topInset={toolbarHeight}
      />
    </Animated.View>
  );
}

/**
 * The map's page, from the start of its download (`mapDownload` null) to the loaded map. While it
 * downloads it draws what `LoadingMap` does: the silhouette, and the status under the greeting. It
 * stays one page, so that when the map arrives only the canvas and the sheet's content change, and
 * the sheet, the toolbar and the buttons riding the sheet stay mounted.
 */
function MapPage({
  initialFocus,
  isError,
  mapDownload,
  offlineMapsAction,
  onRetry,
  presence,
  regionId,
  setRegionId,
  welcome,
}: {
  initialFocus: ChinaMapFocusRequest["bbox"] | null;
  isError: boolean;
  mapDownload: MapDownload | null;
  offlineMapsAction: ReactNode;
  onRetry: () => void;
  presence: MapPresence;
  regionId: ChinaMapRegionId;
  setRegionId: (regionId: ChinaMapRegionId, bbox?: ChinaMapFocusRequest["bbox"] | null) => void;
  welcome: ReactNode;
}) {
  // Each tab is its own map. They share everything else: the levels that unlock their places, and
  // the learner's one location, wallet and journey.
  const chinaMap = mapDownload?.map ?? null;
  const regionCopy = CHINA_MAP_REGION_COPY[regionId];
  const { hanziScript } = useActorUser();
  const levels = useLevels();
  // Until the study data settles nothing counts as studied, so the map renders immediately; the
  // sheet waits for it (and the pronunciation data below) rather than list the wrong places.
  const { data: studiedCharacterData } = useBrowseLevelsStudyGraphemesQuery();
  const studiedCharacters = studiedCharacterData ?? NO_STUDY_DATA_YET;
  // Pronunciation-level lessons count toward the lessons left before a place unlocks.
  const { data: pronouncedGraphemes } = useQuery(usePronouncedGraphemesQueryOptions());
  const levelIndexByCharacter = useMemo(() => createLevelIndexByCharacter(levels), [levels]);
  const lessonsUntilByCharacter = useMemo(
    () =>
      createLessonsUntilByCharacter(
        buildUnitSequence(levels),
        studiedCharacters,
        pronouncedGraphemes ?? NOTHING_PRONOUNCED_YET,
      ),
    [levels, pronouncedGraphemes, studiedCharacters],
  );
  const provinces = useMemo(
    () =>
      chinaMap === null
        ? []
        : createChinaMapPlaceViews(chinaMap, {
            hanziScript,
            // Taiwan, Hong Kong and Macau write in traditional characters; keep their labels that
            // way regardless of the learner's curriculum script. Unlock still uses `hanziScript` so
            // simplified levels match.
            labelHanziScript: isChinaMapTraditionalLabelRegion(regionId)
              ? "traditional"
              : hanziScript,
            lessonsUntilByCharacter,
            levelIndexByCharacter,
            studiedCharacters,
          }),
    [
      chinaMap,
      regionId,
      hanziScript,
      lessonsUntilByCharacter,
      levelIndexByCharacter,
      studiedCharacters,
    ],
  );
  const placeIndex = useMemo(
    () => new Map((mapDownload?.places ?? []).map((place) => [place.adcode, place])),
    [mapDownload],
  );
  const colors = useChinaMapColors();
  // The floating app toolbar overlays the top of every tab's content.
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const toolbarBottomInset = toolbarHeight + useContext(ScrollEdgeTopInsetContext);
  const [selection, setSelection] = useState<ChinaMapSelection | null>(null);
  const [focusRequest, setFocusRequest] = useState<ChinaMapFocusRequest | null>(() =>
    initialFocus === null ? null : { bbox: initialFocus, id: 1 },
  );
  const [showingOptions, setShowingOptions] = useState(false);
  const [travelError, setTravelError] = useState<string | null>(null);
  const openingVisit = useMapOpeningVisit(presence.isToolbarCurrent);
  const [selectionVisit, setSelectionVisit] = useState(openingVisit);
  if (selectionVisit !== openingVisit) {
    // Reset during render: the first committed reopening shows the greeting, even after a city
    // was selected last time. Camera framing and downloaded data remain mounted.
    setSelectionVisit(openingVisit);
    setSelection(null);
    setShowingOptions(false);
    setTravelError(null);
  }
  // The sheet's top edge, which the camera buttons ride just above.
  const sheetTop = useSharedValue(0);
  const pageRef = useAnimatedRef<View>();
  const cameraControlsRef = useRef<ChinaMapCameraControls | null>(null);
  const [disableLockedAreas] = useDeviceSetting("mapDisableLockedAreas");
  const push = useMorphemePanelsHrefNavigator().push.mutate;

  const { state: travelState } = useTravelState();
  const { data: walletData } = useQuery(useHanWalletQueryOptions());
  const wallet = walletData ?? EMPTY_WALLET;
  // Nothing can be bought before the map (and with it its transport) has loaded.
  const travelActions = useTravelActions(mapDownload?.transport ?? NO_TRANSPORT_YET);
  const isTravelBusy =
    travelActions.cancelMutation.isPending ||
    travelActions.chooseStartMutation.isPending ||
    travelActions.purchaseMutation.isPending ||
    travelActions.rideDragonMutation.isPending ||
    travelActions.teleportMutation.isPending;

  const focusedProvince =
    selection === null
      ? null
      : (provinces.find((province) => province.place.adcode === selection.provinceAdcode) ?? null);
  const selectedPlace =
    focusedProvince === null || selection === null
      ? null
      : focusedProvince.place.adcode === selection.placeAdcode
        ? focusedProvince
        : (focusedProvince.children.find((child) => child.place.adcode === selection.placeAdcode) ??
          null);

  const nameOf = useCallback(
    (adcode: number) => {
      const place = placeIndex.get(adcode);
      return place === undefined
        ? String(adcode)
        : hanziScript === "traditional" || isChinaMapTraditionalLabelRegion(place.regionId)
          ? place.traditionalShortName
          : place.shortName;
    },
    [hanziScript, placeIndex],
  );
  const currentAdcode = travelState.kind === "at" ? travelState.adcode : null;
  const currentView =
    currentAdcode === null ? null : (findChinaMapPlaceView(provinces, currentAdcode)?.view ?? null);
  const currentPlace = currentAdcode === null ? undefined : placeIndex.get(currentAdcode);
  // A journey belongs to the map that holds either end of it.
  const isTripOnMap =
    travelState.kind === "in-transit" &&
    (findChinaMapPlaceView(provinces, travelState.trip.originAdcode) !== null ||
      findChinaMapPlaceView(provinces, travelState.trip.destinationAdcode) !== null);

  // Each map has its own coordinates, so the marker and the journey line are drawn only on the map
  // that holds the place, or both ends of the journey.
  const travelOverlay = useMemo((): ChinaMapTravelOverlay => {
    if (travelState.kind === "in-transit") {
      const from = findChinaMapPlaceView(provinces, travelState.trip.originAdcode)?.view;
      const to = findChinaMapPlaceView(provinces, travelState.trip.destinationAdcode)?.view;
      return {
        current: null,
        route:
          from === undefined || to === undefined
            ? null
            : { from: from.place.centroid, progress: travelState.progress, to: to.place.centroid },
      };
    }
    const currentViewOnMap =
      currentAdcode === null
        ? null
        : (findChinaMapPlaceView(provinces, currentAdcode)?.view ?? null);
    return { current: currentViewOnMap?.place.centroid ?? null, route: null };
  }, [currentAdcode, provinces, travelState]);

  const selectedTravelOptions = useMemo((): readonly TravelOption[] => {
    if (currentAdcode === null || selectedPlace === null || mapDownload === null) {
      return [];
    }
    return resolveTravelOptions(currentAdcode, selectedPlace.place.adcode, mapDownload.transport);
  }, [currentAdcode, selectedPlace, mapDownload]);

  const focus = useCallback((bbox: ChinaMapFocusRequest["bbox"]) => {
    setFocusRequest((previous) => ({ bbox, id: (previous?.id ?? 0) + 1 }));
  }, []);

  const select = useCallback((next: ChinaMapSelection | null) => {
    setSelection(next);
    setShowingOptions(false);
    setTravelError(null);
  }, []);

  const showWholeMap = useCallback(() => {
    select(null);
    if (chinaMap !== null) {
      focus([0, 0, chinaMap.width, chinaMap.height]);
    }
  }, [chinaMap, focus, select]);

  /**
   * Frames the learner's surroundings (or, in transit, the whole journey) without selecting. The
   * current place can be on another map: showing it opens that map.
   */
  const showCurrentLocation = useMemo(() => {
    if (travelOverlay.route !== null) {
      const { from, to } = travelOverlay.route;
      const journey: ChinaMapFocusRequest["bbox"] = [
        Math.min(from[0], to[0]),
        Math.min(from[1], to[1]),
        Math.max(from[0], to[0]),
        Math.max(from[1], to[1]),
      ];
      return () => focus(journey);
    }
    if (currentPlace === undefined) {
      return null;
    }
    // A city (or district) and about its width again on every side: its neighbours, as context.
    const [minX, minY, maxX, maxY] = currentPlace.bbox;
    const padding = Math.max(maxX - minX, maxY - minY);
    const surroundings: ChinaMapFocusRequest["bbox"] = [
      minX - padding,
      minY - padding,
      maxX + padding,
      maxY + padding,
    ];
    const currentRegionId = currentPlace.regionId;
    return () => {
      if (currentRegionId !== regionId) {
        setRegionId(currentRegionId, surroundings);
        select(null);
      }
      focus(surroundings);
    };
  }, [currentPlace, focus, regionId, select, setRegionId, travelOverlay.route]);

  const selectRegion = useCallback(
    (nextRegionId: ChinaMapRegionId) => {
      setRegionId(nextRegionId);
      // A selection belongs to the map it was made on; each map opens on the whole of itself.
      // Tapping the open map's tab shows all of it again.
      select(null);
      if (nextRegionId === regionId && chinaMap !== null) {
        focus([0, 0, chinaMap.width, chinaMap.height]);
      }
    },
    [chinaMap, focus, regionId, select, setRegionId],
  );

  const showProvince = useCallback(() => {
    if (focusedProvince !== null) {
      select({
        placeAdcode: focusedProvince.place.adcode,
        provinceAdcode: focusedProvince.place.adcode,
      });
      focus(focusedProvince.place.bbox);
    }
  }, [focus, focusedProvince, select]);

  const selectView = useCallback(
    (view: ChinaMapPlaceView) => {
      // The current place can be on another map: showing it opens that map.
      select({ placeAdcode: view.place.adcode, provinceAdcode: view.province.adcode });
      focus(view.province.bbox);
    },
    [focus, select],
  );

  const onTapMapPoint = useCallback(
    (x: number, y: number, scale: number) => {
      // These maps remain pannable and zoomable while their place content is coming soon.
      if (regionId !== "mainland") {
        return;
      }
      // The learner's marker is a small dot, so a tap anywhere around it opens their place, even
      // from the whole-country view where a tap on a province would only zoom into it.
      if (
        currentView !== null &&
        isChinaMapLocationMarkerHit(currentView.place.centroid, [x, y], scale)
      ) {
        const provinceAdcode = currentView.province.adcode;
        select({ placeAdcode: currentView.place.adcode, provinceAdcode });
        if (provinceAdcode !== selection?.provinceAdcode) {
          focus(currentView.province.bbox);
        }
        return;
      }
      const hit = findChinaMapPlaceAt(provinces, x, y);
      if (hit === null) {
        // Tapping the sea steps back out: from a city to its province, from a province to the whole map.
        if (selection !== null && selection.placeAdcode !== selection.provinceAdcode) {
          showProvince();
        } else if (selection !== null) {
          showWholeMap();
        }
        return;
      }
      // Locked regions stay visible but do not steal taps when the Map setting is on.
      if (
        disableLockedAreas &&
        hit.unlock.status !== "unlocked" &&
        !travelState.visited.has(hit.place.adcode)
      ) {
        return;
      }
      const provinceAdcode = hit.province.adcode;
      // Cities are too small to hit from the whole-country view, so the first tap on a province
      // zooms into it; once zoomed in, a tap selects the city (or district) under the finger.
      if (provinceAdcode !== selection?.provinceAdcode) {
        select({ placeAdcode: provinceAdcode, provinceAdcode });
        focus(hit.province.bbox);
        return;
      }
      select({ placeAdcode: hit.place.adcode, provinceAdcode });
    },
    [
      currentView,
      disableLockedAreas,
      focus,
      provinces,
      regionId,
      select,
      selection,
      showWholeMap,
      showProvince,
      travelState.visited,
    ],
  );

  const onOpenCharacter = useCallback(
    (morpheme: string) => {
      push({
        morpheme,
        prefix: {
          href: "/(nav)/(app)/learn/map/morpheme/[morpheme]",
          params: { view: "mnemonics" },
        },
      });
    },
    [push],
  );

  /** Runs a travel mutation, surfacing a rejection (not enough Han, still travelling…) inline. */
  const runTravel = useCallback(async (run: () => Promise<unknown>) => {
    setTravelError(null);
    try {
      await run();
      setShowingOptions(false);
    } catch (error) {
      setTravelError(ensureError(error).message);
    }
  }, []);

  const reachablePlaces = useMemo(() => {
    const places = getChinaMapVisiblePlaces(provinces).filter(
      (view) => view.unlock.status === "unlocked" || travelState.visited.has(view.place.adcode),
    );
    const origin = currentPlace?.lonLat ?? placeIndex.get(SUGGESTED_START_ADCODE)?.lonLat;
    return origin === undefined
      ? places
      : [...places].sort(
          (a, b) => greatCircleKm(origin, a.place.lonLat) - greatCircleKm(origin, b.place.lonLat),
        );
  }, [currentPlace, placeIndex, provinces, travelState.visited]);

  const upcomingPlaces = useMemo(
    () => getChinaMapUpcomingPlaces(provinces, travelState.visited),
    [provinces, travelState.visited],
  );

  const visitedCount = useMemo(
    () =>
      [...travelState.visited].filter((adcode) => placeIndex.get(adcode)?.regionId === regionId)
        .length,
    [placeIndex, regionId, travelState.visited],
  );

  // On another map than the learner's, the sheet is about that map as a whole: their city's own
  // quests stay on their city's map.
  const quests = <MapQuestsSurface currentAdcode={currentView === null ? null : currentAdcode} />;
  const isComingSoon = regionId !== "mainland";
  let card;
  if (isComingSoon) {
    card = <MapComingSoonSheetContent />;
  } else if (selectedPlace !== null && showingOptions && currentAdcode !== null) {
    card = (
      <ChinaMapTravelOptionsCard
        destination={selectedPlace}
        isBusy={isTravelBusy}
        nameOf={nameOf}
        onBack={() => setShowingOptions(false)}
        onPurchase={(option, redeemHanMiles) =>
          void runTravel(async () => {
            await travelActions.purchaseMutation.mutateAsync({
              destinationAdcode: selectedPlace.place.adcode,
              destinationUnlocked: selectedPlace.unlock.status === "unlocked",
              optionId: option.id,
              redeemHanMiles,
            });
            // Zoom out to the whole journey, with the in-transit card in place of the place card.
            showWholeMap();
          })
        }
        options={selectedTravelOptions}
        originName={nameOf(currentAdcode)}
        wallet={wallet}
      />
    );
  } else if (selectedPlace !== null) {
    card = (
      <ChinaMapPlaceCard
        actions={
          <ChinaMapPlaceTravelActions
            cheapestCostHan={selectedTravelOptions[0]?.costHan ?? null}
            isBusy={isTravelBusy}
            nameOf={nameOf}
            onRideDragon={() =>
              void runTravel(() =>
                travelActions.rideDragonMutation.mutateAsync({
                  destinationAdcode: selectedPlace.place.adcode,
                  destinationUnlocked: selectedPlace.unlock.status === "unlocked",
                }),
              )
            }
            onShowOptions={() => setShowingOptions(true)}
            onStartHere={() =>
              void runTravel(() =>
                travelActions.chooseStartMutation.mutateAsync({
                  adcode: selectedPlace.place.adcode,
                  unlocked: selectedPlace.unlock.status === "unlocked",
                }),
              )
            }
            onTeleport={() =>
              void runTravel(() =>
                travelActions.teleportMutation.mutateAsync({
                  destinationAdcode: selectedPlace.place.adcode,
                }),
              )
            }
            placeNounSingular={regionCopy.getPlaceNoun(selectedPlace.province).singular}
            state={travelState}
            view={selectedPlace}
            wallet={wallet}
          />
        }
        focusedProvince={focusedProvince}
        onClose={() => select(null)}
        onOpenCharacter={onOpenCharacter}
        onSelectProvince={showProvince}
        placeNounPlural={regionCopy.getPlaceNoun(selectedPlace.province).plural}
        view={selectedPlace}
      />
    );
  } else if (isTripOnMap && travelState.kind === "in-transit") {
    card = (
      <ChinaMapInTransitCard
        isBusy={isTravelBusy}
        nameOf={nameOf}
        onCancel={() => void runTravel(() => travelActions.cancelMutation.mutateAsync())}
        quests={quests}
        state={travelState}
      />
    );
  } else if (travelState.kind === "unplaced") {
    card = (
      <ChinaMapChooseStartSheetContent
        availablePlaces={reachablePlaces.slice(0, MAX_PLACE_CHIPS)}
        onSelect={selectView}
        quests={quests}
        upcomingPlaces={upcomingPlaces}
        upcomingPlacesTitle={regionCopy.upcomingPlacesTitle}
      />
    );
  } else {
    card = (
      <ChinaMapOverviewSheetContent
        destinations={reachablePlaces
          .filter((view) => view.place.adcode !== currentAdcode)
          .slice(0, MAX_PLACE_CHIPS)}
        onSelect={selectView}
        placesLabel={regionCopy.placesLabel}
        provinces={provinces}
        provincesLabel={regionCopy.provincesLabel}
        quests={quests}
        upcomingPlaces={upcomingPlaces}
        upcomingPlacesTitle={regionCopy.upcomingPlacesTitle}
        visitedCount={visitedCount}
      />
    );
  }

  // On the map home the greeting heads the sheet, above whatever the map shows with nothing
  // selected; a selected place's card takes the sheet to itself.
  const sheetWelcome = !isComingSoon && selectedPlace === null ? welcome : null;
  // A place card needs more room than the overview, which peeks over the map until dragged up. The
  // home's greeting rests a little higher still, so its Start is never below the fold.
  const sheetDetent: ChinaMapSheetDetent = isComingSoon
    ? "peek"
    : sheetWelcome !== null
      ? "home"
      : selectedPlace !== null || isTripOnMap
        ? "card"
        : "peek";
  // While the map downloads, its silhouette sits above the sheet as it opens (as in `LoadingMap`).
  const openingSheetHeight = useChinaMapOpeningSheetHeight();
  const sheetBottomInset =
    chinaMap === null && sheetDetent === "home"
      ? openingSheetHeight
      : CHINA_MAP_SHEET_HEIGHTS[sheetDetent];
  const controlsBottomInset =
    Platform.OS === "web" ? 16 : sheetBottomInset + CHINA_MAP_SHEET_BUTTONS_GAP;
  // A new learner's onboarding tours the map with its own sheet (`OnboardingSheet`), in this one's
  // place: the map's sheet and the buttons riding it wait for the tour to reach the cards.
  const isOnboardingTour = useIsOnboardingTour();
  const isSheetShown = !isOnboardingTour;
  const isItineraryReady = studiedCharacterData !== undefined && pronouncedGraphemes !== undefined;
  usePublishLearnMapSheetTop({
    isSheetShown,
    pageRef,
    progress: presence.progress,
    sheetTop,
  });

  return (
    // Animated, so that `pageRef` can be measured on the UI thread (`usePublishLearnMapSheetTop`).
    <Animated.View className="flex-1 bg-background" ref={pageRef} testID="map-page">
      {/* The top bar floats over the map, which already insets its content by its height. */}
      <MapToolbar
        isCurrent={presence.isToolbarCurrent}
        onSelectRegion={selectRegion}
        regionId={regionId}
      />
      <MapWorkspace
        controls={
          <ChinaMapSheetSlide progress={presence.progress} testID="map-sheet-buttons-slide">
            {isOnboardingTour ? null : chinaMap === null ? (
              // Where the loaded map's sheet buttons put it: over the sheet's left edge.
              <View
                className="absolute left-4"
                pointerEvents="box-none"
                style={{ bottom: controlsBottomInset }}
                testID="map-more-actions"
              >
                <MapMoreActions camera={null} offlineMaps={offlineMapsAction} />
              </View>
            ) : (
              <ChinaMapSheetButtons
                moreActions={
                  <MapMoreActions
                    camera={{
                      onShowWholeMap: showWholeMap,
                      onZoomIn: () => cameraControlsRef.current?.zoomIn(),
                      onZoomOut: () => cameraControlsRef.current?.zoomOut(),
                      regionName: regionCopy.name,
                    }}
                    offlineMaps={offlineMapsAction}
                  />
                }
                onShowChina={() => focus([0, 0, chinaMap.width, chinaMap.height])}
                onShowCurrentLocation={showCurrentLocation}
                regionName={regionCopy.name}
                sheetTop={sheetTop}
                topInset={toolbarBottomInset}
              />
            )}
          </ChinaMapSheetSlide>
        }
        details={
          <ChinaMapSheetSlide
            className="z-20"
            progress={presence.progress}
            testID="map-sheet-slide"
          >
            {isSheetShown ? (
              <ChinaMapBottomSheet
                animatedPosition={sheetTop}
                corner={
                  <ChinaMapSheetWallet
                    isDepositTarget={presence.isToolbarCurrent}
                    wallet={walletData ?? null}
                  />
                }
                detent={sheetDetent}
                key={openingVisit}
                restingKey={`${regionId}:${selection?.placeAdcode ?? "overview"}:${String(showingOptions)}:${String(sheetWelcome !== null)}`}
                topInset={toolbarBottomInset}
                welcome={sheetWelcome}
              >
                {isComingSoon || travelError === null ? null : (
                  <View
                    className="mx-4 mb-2 rounded-xs border border-red-600 px-3 py-2"
                    testID="china-map-travel-error"
                  >
                    <Text className="text-sm text-red-600">{travelError}</Text>
                  </View>
                )}
                {chinaMap === null && !isComingSoon ? (
                  <MapLoadingStatus isError={isError} onRetry={onRetry} regionId={regionId} />
                ) : isComingSoon || isItineraryReady ? (
                  card
                ) : null}
              </ChinaMapBottomSheet>
            ) : null}
          </ChinaMapSheetSlide>
        }
        map={
          <FrostedBackdropTarget active={presence.isShown} className="flex-1">
            {chinaMap === null ? (
              <ChinaMapLoadingCanvas
                bottomInset={Platform.OS === "web" ? 0 : sheetBottomInset}
                regionId={regionId}
                topInset={Platform.OS === "web" ? 0 : toolbarHeight}
              />
            ) : (
              <ChinaMapCanvas
                bottomInset={Platform.OS === "web" ? 0 : sheetBottomInset}
                cameraControlsRef={cameraControlsRef}
                colors={colors}
                focusedProvince={focusedProvince}
                focusRequest={focusRequest}
                mapHeight={chinaMap.height}
                mapWidth={chinaMap.width}
                onTapMapPoint={onTapMapPoint}
                provinces={provinces}
                regionId={regionId}
                topInset={Platform.OS === "web" ? 0 : toolbarHeight}
                travel={travelOverlay}
              />
            )}
          </FrostedBackdropTarget>
        }
        showDetails={isSheetShown}
        topInset={toolbarBottomInset}
      />
    </Animated.View>
  );
}
